import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import {
  Email,
  Sms,
  Visibility,
  VisibilityOff,
  ArrowBack,
  SecurityOutlined
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { gateway } from "../../gateway";
import { colors } from "../ui/colors";
import { inputStyles } from "../ui/styles/inputStyles";
import { useSnackbar } from "../common/snackbar/useSnackbar";
import { SnackbarAlert } from "../common/SnackbarAlert";

export const ForgotPassword = () => {


  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar()

  const [step, setStep] = useState("method"); // "method" | "input" | "code" | "reset"
  const [method, setMethod] = useState(null); // "email" | "phone"
  const [destination, setDestination] = useState("");
  const [contact, setContact] = useState("");
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("")
  const [secondToken, setSecondToken] = useState("")
  const [tokenUid, setTokenUid] = useState("")

  const inputsRef = useRef([]);

  useEffect(() => {
    setShowContent(false);
    setTimeout(() => setShowContent(true), 200);
    setStep("method");
    setMethod(null);
    setContact("");
    setDestination("");
    setCode(["", "", "", "", ""]);
    setIsCodeComplete(false);
  }, []);

  const handleSendCode = async () => {
    if (!contact) return;
    setLoading(true);
    setError("");
    try {
      const normalizedContact = method === "email" ? contact.trim().toLowerCase() : contact;
      const res = await gateway.post("/users/send-reset-password", {
        [method]: normalizedContact,
        twoFaMethod: method
      });

      setToken(res?.data?.data)
      setDestination(normalizedContact);
      setStep("code");
    } catch (e) {
      setError("Error sending code. Please try again.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[index] = value;
    setCode(next);
    setIsCodeComplete(next.every((d) => d.length === 1));
    if (value && index < code.length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, code.length);
    if (pasted) {
      const arr = pasted.split("");
      while (arr.length < code.length) arr.push("");
      setCode(arr);
      setIsCodeComplete(arr.every((d) => d.length === 1));
      inputsRef.current[Math.min(pasted.length, code.length - 1)]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);

    const codeStr = code.join("");

    try {
      const res = await gateway.post(
        "/users/reset-password",
        { code: codeStr },
        { headers: { Authorization: `${token}` } }
      );

      if (res?.data?.status === 202) {
        setStep("reset");

        const newToken = res?.data?.data?.token;
        setSecondToken(newToken)
        setTokenUid(res?.data?.data?.uid)


        showSnackbar("Great! Change your password", "success");
        setLoading(false);
      }
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 401) {
        showSnackbar("Invalid code. Please try again.", "error");
      } else {
        showSnackbar("Something went wrong. Please try later.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const password = data.get("password");
    const confirm = data.get("confirmPassword");

    if (password !== confirm) {
      showSnackbar("Passwords don't match", "error")
      return
    }

    setLoading(true);
    setError("");
    try {

      const res = await gateway.post("/users/reset-password",
        {
          credentials: {
            password: password,
            confirmPassword: confirm,
            tokenUid: tokenUid
          }
        },
        { headers: { Authorization: `${secondToken}` } }
      );

      if (res?.data?.status === 201) {
        setStep("success");

        setTimeout(() => {
          window.location.href = "/signin";
        }, 3000);
      }

    } catch (e) {
      setError("Error resetting password");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMethod = () => {
    setStep("method");
    setMethod(null);
    setContact("");
    setError("");
  };

  const handleBackToInput = () => {
    setStep("input");
    setCode(["", "", "", "", ""]);
    setIsCodeComplete(false);
    setError("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "630px",
          height: "650px",
          borderRadius: "16px",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          p: { xs: 4, md: 6 },
          textAlign: "center",
          transition: "opacity 0.5s",
          boxShadow: "0 8px 12px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          transform: showContent ? "translateY(0)" : "translateY(10px)",
          opacity: showContent ? 1 : 0,
        }}
      >
        {/* Header Icon */}
        <Box sx={{ display: "inline-flex", mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`,
              boxShadow: "0 8px 24px rgba(251,142,0,0.25)",
            }}
          >
            <SecurityOutlined sx={{ fontSize: 40, color: "white" }} />
          </Box>
        </Box>

        {/* STEP 1: Method Selection */}
        {step === "method" && (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "28px", md: "32px" },
                mb: 2,
                color: colors.gray[800],
              }}
            >
              Reset Your Password
            </Typography>

            <Box sx={{ mb: 3 }}>


              <Typography
                sx={{
                  color: colors.gray[600],
                  fontSize: { xs: "15px", md: "16px" },
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                Select how you'd like to receive your password reset code.
              </Typography>


            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
              {/* Email button */}
              <Button
                onClick={() => {
                  setMethod("email");
                  setStep("input");
                }}
                disabled={loading}
                sx={{
                  borderRadius: "12px",
                  height: "64px",
                  px: 4,
                  mt: 6,
                  background: "transparent",
                  border: `2px solid ${colors.orange[100]}`,
                  color: colors.gray[700],
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  transition: "all 200ms ease-out",
                  "&:hover": {
                    background: "rgba(251,142,0,0.04)",
                    border: `2px solid ${colors.orange.base}`,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 16px rgba(251,142,0,0.15)",
                  },
                }}
                variant="outlined"
                disableElevation
                fullWidth
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "flex-start" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(251,142,0,0.1)",
                    }}
                  >
                    <Email sx={{ fontSize: 20, color: colors.orange.base }} />
                  </Box>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.gray[800] }}>EMAIL</Typography>
                    <Typography sx={{ fontSize: "14px", color: colors.gray[500] }}>Send code to your email address</Typography>
                  </Box>
                </Box>
              </Button>

              {/* SMS button */}
              <Button
                onClick={() => {
                  setMethod("phone");
                  setStep("input");
                }}
                disabled={loading}
                sx={{
                  borderRadius: "12px",
                  height: "64px",
                  px: 4,
                  background: "transparent",
                  border: `2px solid ${colors.orange[100]}`,
                  color: colors.gray[700],
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  transition: "all 200ms ease-out",
                  "&:hover": {
                    background: "rgba(251,142,0,0.04)",
                    border: `2px solid ${colors.orange.base}`,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 16px rgba(251,142,0,0.15)",
                  },
                }}
                variant="outlined"
                disableElevation
                fullWidth
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "flex-start" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(251,142,0,0.1)",
                    }}
                  >
                    <Sms sx={{ fontSize: 20, color: colors.orange.base }} />
                  </Box>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.gray[800] }}>SMS</Typography>
                    <Typography sx={{ fontSize: "14px", color: colors.gray[500] }}>Send code via text message</Typography>
                  </Box>
                </Box>
              </Button>
            </Box>

            <Button
              fullWidth
              variant="text"
              component={RouterLink}
              to="/signin"
              sx={{
                py: 1,
                color: colors.orange.base,
                fontSize: "14px",
                fontWeight: 500,
                "&:hover": { backgroundColor: colors.orange[50] },
              }}
            >
              Back to Sign In
            </Button>
          </>
        )}

        {/* STEP 2: Input Contact */}
        {step === "input" && (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "26px", md: "30px" },
                mb: 2,
                color: colors.gray[800],
              }}
            >
              Enter Your {method === "email" ? "Email" : "Phone"}
            </Typography>

            <Typography
              sx={{
                color: colors.gray[600],
                fontSize: { xs: "15px", md: "16px" },
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              We'll send a verification code to your {method}.
            </Typography>

            <Typography
              sx={{
                color: colors.gray[500],
                fontSize: "14px",
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Make sure it’s the same {method} you registered with, otherwise the code won’t arrive.
            </Typography>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", flexDirection: "column" }}>
              <TextField
                fullWidth
                type={method === "email" ? "email" : "tel"}
                label={method === "email" ? "Email Address" : "Phone Number"}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                error={!!error}
                helperText={error}
                sx={inputStyles}
                autoFocus
              />

              <Box>
                <Button
                  onClick={handleSendCode}
                  disabled={loading || !contact}
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.5,
                    mb: 3,
                    mt: 8,
                    borderRadius: "12px",
                    backgroundColor: colors.inverse.base,
                    color: colors.inverse.text,
                    fontSize: "16px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
                    "&:disabled": { backgroundColor: colors.gray[200], color: colors.gray[400] },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "SEND CODE"}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToMethod}
                  sx={{
                    py: 1,
                    color: colors.orange.base,
                    fontSize: "14px",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: colors.orange[50] },
                  }}
                >
                  Back
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* STEP 3: Code Verification */}
        {step === "code" && (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "26px", md: "30px" },
                mb: 2,
                color: colors.gray[800],
              }}
            >
              Enter Verification Code
            </Typography>

            <Typography
              sx={{
                color: colors.gray[600],
                fontSize: { xs: "15px", md: "16px" },
                fontWeight: 500,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              We sent a code to your {method}: <br />
              <strong>{destination}</strong>
            </Typography>

            <Typography
              sx={{
                color: colors.gray[500],
                fontSize: "14px",
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Didn’t receive it? It may take up to a minute.
              Check your spam/junk folder if you chose email.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4 }}>
              {code.map((digit, index) => (
                <TextField
                  key={index}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  inputRef={(ref) => (inputsRef.current[index] = ref)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    style: { textAlign: "center", fontSize: "20px", fontWeight: 600 }
                  }}
                  sx={{
                    width: 56,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: colors.warmWhite?.base || "var(--fx-surface-sunken, #F8FAFC)",
                      "& fieldset": { borderColor: colors.orange[200], borderWidth: "2px" },
                      "&:hover fieldset": { borderColor: colors.orange[400] },
                      "&.Mui-focused fieldset": { borderColor: colors.orange.base },
                    },
                  }}
                />
              ))}
            </Box>

            <Button
              onClick={handleVerifyCode}
              disabled={!isCodeComplete || loading}
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: colors.inverse.base,
                color: colors.inverse.text,
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "none",
                "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
                "&:disabled": { backgroundColor: colors.gray[200], color: colors.gray[400] },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "VERIFY CODE"}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={handleBackToInput}
              sx={{
                py: 1,
                color: colors.orange.base,
                fontSize: "14px",
                fontWeight: 500,
                "&:hover": { backgroundColor: colors.orange[50] },
              }}
            >
              Resend Code
            </Button>
          </>
        )}

        {/* STEP 4: Reset Password */}
        {step === "reset" && (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "26px", md: "30px" },
                mb: 2,
                color: colors.gray[800],
              }}
            >
              Set New Password
            </Typography>

            <Typography
              sx={{
                color: colors.gray[600],
                fontSize: { xs: "15px", md: "16px" },
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Create a strong new password for your account.
            </Typography>

            <Typography
              sx={{
                color: colors.gray[500],
                fontSize: "14px",
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Use at least 8 characters, with a mix of uppercase, lowercase, numbers,
              and symbols for best security.
            </Typography>

            <Box sx={{ gap: 5 }} component="form" onSubmit={handleResetPassword}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                sx={inputStyles}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: colors.gray[400] }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                error={!!error}
                helperText={error}
                sx={inputStyles}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      sx={{ color: colors.gray[400] }}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 3,
                  mt: 5,
                  borderRadius: "12px",
                  backgroundColor: colors.inverse.base,
                  color: colors.inverse.text,
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
                  "&:disabled": { backgroundColor: colors.gray[200], color: colors.gray[400] },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "RESET PASSWORD"}
              </Button>
            </Box>
          </>
        )}

        {/* Success */}
        {step === "success" && (
          <>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "26px", md: "30px" },
                mb: 2,
                color: colors.gray[800],
              }}
            >
              Password Changed!
            </Typography>

            <Typography
              sx={{
                color: colors.gray[600],
                fontSize: { xs: "15px", md: "16px" },
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Your password has been successfully updated.
            </Typography>

            <Typography
              sx={{
                color: colors.gray[500],
                fontSize: "14px",
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              You will be redirected to the home page shortly, or click below to continue.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => (window.location.href = "/")}
              sx={{
                py: 1.5,
                mb: 3,
                mt: 5,
                borderRadius: "12px",
                backgroundColor: colors.inverse.base,
                color: colors.inverse.text,
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "none",
                "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
              }}
            >
              Go to Home
            </Button>
          </>
        )}
      </Box>

      <SnackbarAlert
        openState={snackbar.open}
        setOpenState={closeSnackbar}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};